/**
 * ForwardingSetup — the recommended way to get an existing business number
 * answered by a Genie, in about three minutes.
 *
 * The alternative in this app is porting: paperwork, a support queue, and up to
 * five business days. Forwarding is the same outcome today, so it leads.
 *
 * Four steps, none of which need a person on our side:
 *   1. the Help Genie number (already provisioned, shown to copy)
 *   2. country and carrier
 *   3. forward everything, or only calls you cannot take
 *   4. the exact dial code, then a real test call that proves it worked
 */
import { useMemo, useState } from "react";
import { Check, Copy, Loader2, Phone, PhoneCall, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  FORWARDING_CARRIERS,
  FORWARDING_COUNTRIES,
  FORWARDING_FALLBACK,
  carriersForCountry,
  renderCode,
} from "@/data/phone/forwardingCarriers";
import { useOutboundCall } from "@/hooks/phone/useOutboundCall";
import { cn } from "@/lib/utils";
import { ActivationFunnelService } from "@/services/analytics/ActivationFunnelService";
import type { Agent } from "@/types/agent/agent";
import type { ForwardingCode, ForwardingCountry, ForwardingMode, ForwardingVerification } from "@/types/phone/forwarding";

interface ForwardingSetupProps {
  genie: Agent;
  /** The Help Genie number calls will be forwarded TO. */
  helpGenieNumber: string;
  /** The provisioned number's ElevenLabs phone-number id, needed to place the test call. Not used when listening. */
  elevenlabsPhoneNumberId?: string;
  /** Pre-fill from the business profile when we know it. */
  defaultCountry?: ForwardingCountry;
  /** "answer": the Genie takes the call. "listen": a Listener Genie records calls your team takes. */
  variant?: "answer" | "listen";
}

const VARIANT_COPY = {
  answer: {
    heroTitle: "Your business number, answered today",
    heroBody:
      "Forwarding takes about three minutes on your own phone and you can undo it just as fast. Nothing changes with your carrier and you keep your number exactly as it is.",
    modeQuestion: "3. What should your Genie answer?",
    modes: {
      everything: {
        title: "Forward everything",
        body: "Every call to your business number is answered by your Genie. Best when nobody is on the phones.",
      },
      unanswered: {
        title: "Only calls you cannot take",
        body: "Your phone rings first. Your Genie picks up when you do not answer or you are already on a call. This is the after-hours Genie.",
      },
    },
  },
  listen: {
    heroTitle: "Have your business number's calls listened to",
    heroBody:
      "Forward your business number here and every call still rings your team, but your Genie listens and analyses it. It takes about three minutes and you can undo it just as fast. Make sure the numbers your Genie rings are not the number you forward, or the call loops back.",
    modeQuestion: "3. Which calls should your Genie listen to?",
    modes: {
      everything: {
        title: "Forward everything",
        body: "Every call comes through your Genie, rings the numbers you set in Call routing, and is recorded and analysed.",
      },
      unanswered: {
        title: "Only calls you cannot take",
        body: "Your phone rings first. Calls you miss go to the numbers you set in Call routing, or to voicemail, and are analysed.",
      },
    },
  },
} satisfies Record<string, { heroTitle: string; heroBody: string; modeQuestion: string; modes: Record<ForwardingMode, { title: string; body: string }> }>;

/** A dial code, big enough to read off the screen while holding a phone. */
const CodeRow = ({ code, number }: { code: ForwardingCode; number: string }) => {
  const [copied, setCopied] = useState(false);
  const dial = renderCode(code.pattern, number);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(dial);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy. Type the code into your phone's keypad instead.");
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-sm font-medium text-gray-700">{code.label}</p>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <code className="rounded-lg bg-genie-background px-3 py-2 text-lg font-bold tracking-wide text-[#222B45]">
          {dial}
        </code>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-genie-primary hover:underline"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <p className="mt-2 text-xs text-gray-500">
        To undo it later, dial{" "}
        <span className="font-semibold text-gray-700">{code.cancelPattern}</span>
      </p>
    </div>
  );
};

const SelectTile = ({
  title,
  body,
  selected,
  onSelect,
}: {
  title: string;
  body?: string;
  selected: boolean;
  onSelect: () => void;
}) => (
  <button
    type="button"
    onClick={onSelect}
    className={cn(
      "flex flex-col items-start gap-1 rounded-xl border-2 bg-white p-4 text-left transition-colors",
      selected ? "border-genie-primary bg-genie-primary/5" : "border-gray-200 hover:border-genie-primary/40",
    )}
  >
    <span className="text-sm font-semibold text-gray-900">{title}</span>
    {body && <span className="text-xs leading-snug text-gray-500">{body}</span>}
  </button>
);

export const ForwardingSetup = ({
  genie,
  helpGenieNumber,
  elevenlabsPhoneNumberId,
  defaultCountry = "NZ",
  variant = "answer",
}: ForwardingSetupProps) => {
  const copy = VARIANT_COPY[variant];
  const canTestCall = variant === "answer" && !!elevenlabsPhoneNumberId;
  const [country, setCountry] = useState<ForwardingCountry>(defaultCountry);
  const [carrierId, setCarrierId] = useState<string>(FORWARDING_FALLBACK.id);
  const [mode, setMode] = useState<ForwardingMode>("everything");
  const [businessNumber, setBusinessNumber] = useState("");
  const [verification, setVerification] = useState<ForwardingVerification>({ state: "idle" });

  const { makeCall } = useOutboundCall();

  const carriers = useMemo(() => carriersForCountry(country), [country]);
  const carrier = FORWARDING_CARRIERS.find((c) => c.id === carrierId) ?? null;

  const codes: ForwardingCode[] = carrier
    ? carrier.codes[mode]
    : mode === "everything"
      ? [FORWARDING_FALLBACK.genericCodes[0]]
      : [FORWARDING_FALLBACK.genericCodes[1], FORWARDING_FALLBACK.genericCodes[2]];

  const canVerify = businessNumber.replace(/\D/g, "").length >= 8;

  const runTestCall = async () => {
    if (!canVerify || !elevenlabsPhoneNumberId) return;
    setVerification({ state: "calling" });

    const result = await makeCall(genie, elevenlabsPhoneNumberId, businessNumber);

    if (result?.success) {
      // The call left our side. Whether forwarding worked is something only the
      // customer can confirm, so we ask rather than claim it.
      setVerification({ state: "passed", at: new Date().toISOString() });
      ActivationFunnelService.forwardingVerified(genie.id, carrierId, mode);
    } else {
      setVerification({
        state: "failed",
        reason: result?.message ?? "We could not place the test call. Try again in a moment.",
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Why this path */}
      <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
        <div className="rounded-[14px] bg-white p-5">
          <div className="flex items-start gap-3">
            <Phone className="mt-0.5 h-5 w-5 flex-shrink-0 text-genie-primary" />
            <div>
              <p className="text-base font-semibold text-gray-900">{copy.heroTitle}</p>
              <p className="mt-1 text-sm text-gray-600">{copy.heroBody}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Step 1 — the number to forward to */}
      <section>
        <h4 className="text-sm font-semibold text-gray-700">1. Forward your calls to this number</h4>
        <div className="mt-2 flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-4">
          <code className="text-lg font-bold tracking-wide text-[#222B45]">{helpGenieNumber}</code>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(helpGenieNumber);
              toast.success("Number copied");
            }}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-genie-primary hover:underline"
          >
            <Copy className="h-3.5 w-3.5" />
            Copy
          </button>
        </div>
      </section>

      {/* Step 2 — country + carrier */}
      <section>
        <h4 className="text-sm font-semibold text-gray-700">2. Who is your phone with?</h4>

        <div className="mt-2 flex gap-2">
          {FORWARDING_COUNTRIES.map(({ code, name }) => (
            <button
              key={code}
              type="button"
              onClick={() => {
                setCountry(code);
                setCarrierId(FORWARDING_FALLBACK.id);
              }}
              className={cn(
                "rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-colors",
                country === code
                  ? "border-genie-primary bg-genie-primary/5 text-genie-primary"
                  : "border-gray-200 text-gray-600 hover:border-genie-primary/40",
              )}
            >
              {name}
            </button>
          ))}
        </div>

        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {carriers.map((c) => (
            <SelectTile
              key={c.id}
              title={c.name}
              selected={carrierId === c.id}
              onSelect={() => setCarrierId(c.id)}
            />
          ))}
          <SelectTile
            title={FORWARDING_FALLBACK.name}
            selected={carrierId === FORWARDING_FALLBACK.id}
            onSelect={() => setCarrierId(FORWARDING_FALLBACK.id)}
          />
        </div>
      </section>

      {/* Step 3 — what to forward */}
      <section>
        <h4 className="text-sm font-semibold text-gray-700">{copy.modeQuestion}</h4>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {(["everything", "unanswered"] as ForwardingMode[]).map((m) => (
            <SelectTile
              key={m}
              title={copy.modes[m].title}
              body={copy.modes[m].body}
              selected={mode === m}
              onSelect={() => setMode(m)}
            />
          ))}
        </div>
      </section>

      {/* Step 4 — the codes */}
      <section>
        <h4 className="text-sm font-semibold text-gray-700">
          4. Dial {codes.length > 1 ? "these codes" : "this code"} from the phone you want forwarded
        </h4>
        <div className="mt-2 space-y-2">
          {codes.map((code) => (
            <CodeRow key={code.pattern} code={code} number={helpGenieNumber} />
          ))}
        </div>

        {carrier && (
          <p className="mt-3 text-xs text-gray-500">
            Prefer to tap than dial? {carrier.appPath}.
            {mode === "unanswered" && " A set opening-hours schedule is set in that app too, rather than by a code."}
          </p>
        )}
        {carrier?.note && <p className="mt-1 text-xs text-gray-500">{carrier.note}</p>}

        {!carrier && (
          <div className="mt-3 rounded-xl border border-gray-200 bg-genie-background p-4">
            <p className="text-sm font-medium text-gray-700">If that code does not work</p>
            <ul className="mt-2 list-disc space-y-1.5 pl-5 text-xs leading-relaxed text-gray-600">
              {FORWARDING_FALLBACK.howToFind.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Step 5 — prove it */}
      {canTestCall && (
        <section>
          <h4 className="text-sm font-semibold text-gray-700">5. Check it worked</h4>
          <p className="mt-1 text-xs text-gray-500">
            We will call your business number. If forwarding is on, your Genie answers instead of your
            phone ringing.
          </p>
  
          <div className="mt-2 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <label className="mb-2 block text-sm font-semibold text-gray-700" htmlFor="hg-forward-test-number">
                Your business number
              </label>
              <input
                id="hg-forward-test-number"
                type="tel"
                inputMode="tel"
                value={businessNumber}
                onChange={(e) => {
                  setBusinessNumber(e.target.value);
                  setVerification({ state: "idle" });
                }}
                placeholder={FORWARDING_COUNTRIES.find((c) => c.code === country)?.placeholder}
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-genie-primary"
              />
            </div>
  
            <button
              type="button"
              onClick={runTestCall}
              disabled={!canVerify || verification.state === "calling"}
              className="inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-genie-primary/30 transition-all duration-200 hover:opacity-95 disabled:opacity-50"
              style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
            >
              {verification.state === "calling" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <PhoneCall className="h-4 w-4" />
              )}
              {verification.state === "calling" ? "Calling..." : "Place a test call"}
            </button>
          </div>
  
          {verification.state === "passed" && (
            <div className="mt-3 rounded-xl border border-genie-primary/30 bg-genie-primary/5 p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-genie-primary">
                <Check className="h-4 w-4" />
                Calling you now
              </p>
              <p className="mt-1 text-xs text-gray-600">
                If your Genie answers, forwarding is live and you are done. If your own phone rings
                instead, the code has not taken effect yet. Redial it and try again.
              </p>
              <button
                type="button"
                onClick={() => setVerification({ state: "idle" })}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-genie-primary hover:underline"
              >
                <RotateCcw className="h-3 w-3" />
                Test again
              </button>
            </div>
          )}
  
          {verification.state === "failed" && (
            <p className="mt-3 text-sm text-red-600">{verification.reason}</p>
          )}
        </section>
      )}
    </div>
  );
};

export default ForwardingSetup;
